import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import WhatsAppBooking from '../components/common/WhatsAppBooking';

const About = () => {
  const { t } = useTranslation();
  
  const team = [
    {
      name: t('about.team.members.dentist.name'),
      role: t('about.team.members.dentist.role'),
      bio: t('about.team.members.dentist.bio'),
      image: 'https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg'
    },
    {
      name: t('about.team.members.hygienist1.name'),
      role: t('about.team.members.hygienist1.role'),
      bio: t('about.team.members.hygienist1.bio'),
      image: 'https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg'
    },
    {
      name: t('about.team.members.hygienist2.name'),
      role: t('about.team.members.hygienist2.role'),
      bio: t('about.team.members.hygienist2.bio'),
      image: 'https://images.pexels.com/photos/5214952/pexels-photo-5214952.jpeg'
    }
  ];
  
  return (
    <>
      {/* About Hero */}
      <div className="bg-primary-600 pt-32 pb-20 px-4">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-6">{t('about.title')}</h1>
            <p className="text-xl text-white/80">{t('about.subtitle')}</p>
          </div>
        </div>
      </div>
      
      {/* About Content */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('about.title')}</h2>
              <p className="text-gray-600 mb-8">{t('about.description')}</p>
              
              <h3 className="text-xl font-semibold mb-4">{t('about.mission.title')}</h3>
              <p className="text-gray-600 mb-6">{t('about.mission.description')}</p>
              
              <ul className="mb-8 space-y-2">
                {t('about.values', { returnObjects: true }).map((value: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2 mt-1 bg-primary-100 text-primary-600 p-1 rounded-full">
                      <Check size={14} />
                    </span>
                    <span>{value}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="https://images.pexels.com/photos/3846005/pexels-photo-3846005.jpeg" 
                  alt="Modern dental clinic" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 my-20">
            <div className="text-center p-6 bg-primary-50 rounded-lg">
              <p className="text-4xl font-bold text-primary-600">{t('about.stats.experience.value')}</p>
              <p className="text-gray-600">{t('about.stats.experience.label')}</p>
            </div>
            <div className="text-center p-6 bg-primary-50 rounded-lg">
              <p className="text-4xl font-bold text-primary-600">{t('about.stats.patients.value')}</p>
              <p className="text-gray-600">{t('about.stats.patients.label')}</p>
            </div>
            <div className="text-center p-6 bg-primary-50 rounded-lg">
              <p className="text-4xl font-bold text-primary-600">{t('about.stats.experts.value')}</p>
              <p className="text-gray-600">{t('about.stats.experts.label')}</p>
            </div>
            <div className="text-center p-6 bg-primary-50 rounded-lg">
              <p className="text-4xl font-bold text-primary-600">{t('about.stats.rooms.value')}</p>
              <p className="text-gray-600">{t('about.stats.rooms.label')}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-title">{t('about.team.title')}</h2>
            <p className="max-w-3xl mx-auto text-lg text-gray-600">
              {t('about.team.description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="card text-center group">
                <div className="mb-6 overflow-hidden rounded-full w-32 h-32 mx-auto">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1 group-hover:text-primary-600 transition-colors duration-300">
                  {member.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">{member.role}</p>
                <p className="text-gray-600">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <WhatsAppBooking />
    </>
  );
};

export default About;